import {
  BrowserCacheLocation,
  Configuration,
  LogLevel,
} from "@azure/msal-browser";
import localforage from "localforage";
import { getActivePinia } from "pinia";
import { computed, reactive, ref } from "vue";
import { VueNavigationClient } from "~/router/helpers";
import getMainRouter from "~/router/mainRouter";
import { authService } from "~/services/authService";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { getEnvConfig } from "~/services/appConfig";
import { clearMsalStorage } from "~/utils/authRecovery";
import {
  AuthError,
  AuthErrorType,
  AuthState as AuthStateEnum,
  AuthStoreType,
  MaybeAccount,
} from "~/types/auth";

type ConfigInfo = {
  scopes: string[];
  config: Configuration;
};

interface AuthStoreError {
  type: AuthErrorType;
  message: string;
  timestamp: Date;
  retryable: boolean;
}

const getConfigInfo = (loginType: AuthStoreType): ConfigInfo => {
  const envConfig = getEnvConfig();
  let authority = "";
  const baseConfig = {
    auth: {
      redirectUri: envConfig.entraRedirect,
      postLogoutRedirectUri: "/",
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      loggerOptions: {
        logLevel: LogLevel.Info,
        loggerCallback,
      },
    },
  };
  switch (loginType as any) {
    case AuthStoreType.Hmb:
    case "hmb":
      authority = `${envConfig.authAuthority}/${envConfig.authHmbPolicyId}`;
      return {
        scopes: [
          `${envConfig.authDomain}/${envConfig.authHmbAppId}/${envConfig.authHmbScope}`,
        ],
        config: {
          ...baseConfig,
          auth: {
            clientId: envConfig.authHmbAppId,
            authority: authority,
            knownAuthorities: [authority],
            ...baseConfig.auth,
          },
        },
      };
    case AuthStoreType.User:
      authority = `${envConfig.authAuthority}/${envConfig.authUserPolicyId}`;
      return {
        scopes: [
          `${envConfig.authDomain}/${envConfig.authUserAppId}/${envConfig.authUserScope}`,
        ],
        config: {
          ...baseConfig,
          auth: {
            clientId: envConfig.authUserAppId,
            authority: authority,
            knownAuthorities: [authority],
            ...baseConfig.auth,
          },
        },
      };
    case AuthStoreType.SignUp:
      authority = `${envConfig.authAuthority}/${envConfig.authSignupPolicyId}`;
      return {
        scopes: [
          `${envConfig.authDomain}/${envConfig.authUserAppId}/${envConfig.authUserScope}`,
        ],
        config: {
          ...baseConfig,
          auth: {
            clientId: envConfig.authUserAppId,
            authority: authority,
            knownAuthorities: [authority],
            ...baseConfig.auth,
          },
        },
      };
    default:
      throw new Error("Invalid login type");
  }
};

const KeyForLocalForage = "authStoreType";

const currentState = ref<AuthStateEnum>(AuthStateEnum.UNINITIALIZED);
const error = ref<AuthStoreError | null>(null);
const account = ref<MaybeAccount>(null);
const authStoreType = ref<AuthStoreType>();
const signUpFlow = ref<boolean>(false);

const initialized = computed(
  () => currentState.value !== AuthStateEnum.UNINITIALIZED,
);
const isAuthenticated = computed(
  () => currentState.value === AuthStateEnum.AUTHENTICATED && !!account.value,
);
const isLoading = computed(
  () =>
    currentState.value === AuthStateEnum.INITIALIZING ||
    currentState.value === AuthStateEnum.LOGGING_IN,
);

let configInfo: ConfigInfo;

const setState = (newState: AuthStateEnum, authError?: AuthError) => {
  currentState.value = newState;

  if (authError) {
    error.value = {
      type: authError.type,
      message: authError.message,
      timestamp: new Date(),
      retryable: isRetryableError(authError.type),
    };
  } else if (newState !== AuthStateEnum.ERROR) {
    error.value = null;
  }
};

const isRetryableError = (errorType: AuthErrorType): boolean => {
  return [
    AuthErrorType.INTERACTION_IN_PROGRESS,
    AuthErrorType.TOKEN_ACQUISITION_FAILED,
  ].includes(errorType);
};

const createAuthError = (
  err: unknown,
  errorType: AuthErrorType,
  fallbackMessage: string,
): AuthError => {
  if (err instanceof AuthError) {
    return err;
  }

  const errorMessage = err instanceof Error ? err.message : fallbackMessage;
  return new AuthError(errorType, errorMessage);
};

async function setup(): Promise<AuthStoreType | undefined> {
  if (currentState.value === AuthStateEnum.INITIALIZING) {
    return new Promise((resolve, reject) => {
      const startTime = Date.now();
      const timeout = 30000;

      const checkState = () => {
        if (currentState.value !== AuthStateEnum.INITIALIZING) {
          resolve(authStoreType.value);
        } else if (Date.now() - startTime > timeout) {
          reject(
            new AuthError(
              AuthErrorType.INITIALIZATION_FAILED,
              "Initialization timeout - setup took too long",
            ),
          );
        } else {
          setTimeout(checkState, 100);
        }
      };
      checkState();
    });
  }

  if (currentState.value !== AuthStateEnum.UNINITIALIZED) {
    return authStoreType.value;
  }

  setState(AuthStateEnum.INITIALIZING);

  try {
    const loginType = (await localforage.getItem(KeyForLocalForage)) as
      | AuthStoreType
      | undefined;

    if (loginType) {
      await initializeInternal(loginType);
    }

    const activeAccount = authService.getActiveAccount();
    if (activeAccount && !account.value) {
      account.value = activeAccount;
    }

    // If a SignUp flow was interrupted (e.g., user cancelled B2C),
    // clear the stored login type so the app doesn't keep re-attempting signup on load.
    if (!account.value && loginType === AuthStoreType.SignUp) {
      try {
        await localforage.removeItem(KeyForLocalForage);
      } catch (e) {
        console.warn("Failed to clear interrupted SignUp login type:", e);
      }
      setState(AuthStateEnum.INITIALIZED);
      return undefined;
    }

    setState(
      account.value ? AuthStateEnum.AUTHENTICATED : AuthStateEnum.INITIALIZED,
    );
    return loginType;
  } catch (err) {
    const authError = createAuthError(
      err,
      AuthErrorType.INITIALIZATION_FAILED,
      "Unknown initialization error",
    );
    setState(AuthStateEnum.ERROR, authError);
    throw authError;
  }
}

async function initializeInternal(loginType: AuthStoreType): Promise<void> {
  configInfo = getConfigInfo(loginType);

  if (
    authStoreType.value === loginType &&
    currentState.value >= AuthStateEnum.INITIALIZED
  ) {
    account.value = authService.getActiveAccount();
    return;
  }

  if (authStoreType.value && authStoreType.value !== loginType) {
    await clearAuthenticationState();
  }

  account.value = null;

  try {
    const activeAccount = await authService.initialize(
      new VueNavigationClient(getMainRouter()),
      configInfo.config,
      configInfo.scopes,
    );

    account.value = activeAccount;
    authStoreType.value = loginType;

    await localforage.setItem(KeyForLocalForage, loginType);
  } catch (err) {
    const authError = createAuthError(
      err,
      AuthErrorType.INITIALIZATION_FAILED,
      "Unknown initialization error",
    );
    throw authError;
  }
}

async function login(loginType: AuthStoreType): Promise<void> {
  if (sessionStorage.getItem("redirect_handling_in_progress")) {
    throw new AuthError(
      AuthErrorType.INTERACTION_IN_PROGRESS,
      "Cannot login during redirect handling",
    );
  }

  if (currentState.value === AuthStateEnum.LOGGING_IN) {
    return;
  }

  setState(AuthStateEnum.LOGGING_IN);

  try {
    await initializeInternal(loginType);

    const existingAccount = authService.getActiveAccount();
    if (existingAccount) {
      // Clear any stuck interaction state so the next token acquisition attempt is not blocked.
      // This is important when login() is called after a 401 error — the user is still
      // "authenticated" per MSAL (account in cache) but the interaction lock may be stuck,
      // which would prevent silent token refresh and cause repeated 401 errors.
      authService.clearInteractionState();
      account.value = existingAccount;
      setState(AuthStateEnum.AUTHENTICATED);
      return;
    }

    if (account.value && currentState.value === AuthStateEnum.AUTHENTICATED) {
      return;
    }

    account.value = null;

    const result = await authService.login();
    account.value = result;

    useMyInfoStore().clear();

    setState(result ? AuthStateEnum.AUTHENTICATED : AuthStateEnum.INITIALIZED);
  } catch (err) {
    const authError = createAuthError(
      err,
      AuthErrorType.TOKEN_ACQUISITION_FAILED,
      "Login failed",
    );

    if (authError.type === AuthErrorType.INTERACTION_IN_PROGRESS) {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      authService.clearInteractionState();

      try {
        const retry = await authService.login();
        account.value = retry;
        setState(
          retry ? AuthStateEnum.AUTHENTICATED : AuthStateEnum.INITIALIZED,
        );

        return;
      } catch (retryError) {
        console.warn("Retry login also failed:", retryError);
      }
    }

    setState(AuthStateEnum.ERROR, authError);
    throw authError;
  }
}

async function silentLogin(loginType: AuthStoreType): Promise<void> {
  if (currentState.value === AuthStateEnum.LOGGING_IN) {
    throw new AuthError(
      AuthErrorType.INTERACTION_IN_PROGRESS,
      "Login already in progress",
    );
  }

  setState(AuthStateEnum.LOGGING_IN);

  try {
    await initializeInternal(loginType);

    const result = await authService.silentLogin();
    account.value = result;

    setState(result ? AuthStateEnum.AUTHENTICATED : AuthStateEnum.INITIALIZED);
  } catch (err) {
    const authError = createAuthError(
      err,
      AuthErrorType.TOKEN_ACQUISITION_FAILED,
      "Silent login failed",
    );
    setState(AuthStateEnum.ERROR, authError);
    throw authError;
  }
}

async function logout(): Promise<void> {
  const { trackLogout } = await import("~/utils/activityTracker");
  await trackLogout();

  if (getActivePinia()) {
    useMyInfoStore().clear();
  }

  await authService.logout();

  await clearAuthenticationState();
  setState(AuthStateEnum.LOGGED_OUT);
}

async function clearAuthenticationState(): Promise<void> {
  if (getActivePinia()) {
    useMyInfoStore().clear();
  }

  authService.clearAuthenticationState();

  try {
    await localforage.removeItem(KeyForLocalForage);
  } catch (e) {
    console.warn("Failed to clear persisted authStoreType:", e);
  }

  account.value = null;
  authStoreType.value = undefined;
  error.value = null;

  setState(AuthStateEnum.UNINITIALIZED);
}

async function getAccessTokenAsync(): Promise<string | null> {
  try {
    return await authService.getAccessTokenAsync();
  } catch (err) {
    const authError =
      err instanceof AuthError
        ? err
        : createAuthError(
            err,
            AuthErrorType.TOKEN_ACQUISITION_FAILED,
            "Token acquisition failed",
          );

    if (authError.type === AuthErrorType.INTERACTION_IN_PROGRESS) {
      // Clear the stuck interaction state so the next attempt can proceed
      authService.clearInteractionState();
      return null;
    }

    // Anonymous sessions have no MSAL instance — not an application error.
    if (
      !account.value &&
      authError.type === AuthErrorType.INITIALIZATION_FAILED
    ) {
      return null;
    }

    setState(AuthStateEnum.ERROR, authError);
    return null;
  }
}

async function retryLastOperation(): Promise<void> {
  if (!error.value?.retryable) {
    throw new Error("Last operation is not retryable");
  }

  const lastAuthType = authStoreType.value ?? AuthStoreType.User;

  switch (error.value.type) {
    case AuthErrorType.INTERACTION_IN_PROGRESS:
      await new Promise((resolve) => setTimeout(resolve, 2000));
      await login(lastAuthType);
      break;
    case AuthErrorType.TOKEN_ACQUISITION_FAILED:
      await getAccessTokenAsync();
      break;
    default:
      throw new Error(`Cannot retry error type: ${error.value.type}`);
  }
}

function loggerCallback(level: LogLevel, message: string) {
  if (level > LogLevel.Warning) {
    return;
  }

  const parts = message.split(" : ");
  const text = parts.pop() ?? message;

  switch (level) {
    case LogLevel.Error:
      return console.error(text);

    case LogLevel.Warning:
      return console.warn(text);
  }
}

export const authStore = reactive({
  state: currentState,
  error,
  account,
  initialized,
  isAuthenticated,
  isLoading,
  authStoreType,
  signUpFlow,

  setup,
  login,
  silentLogin,
  logout,
  clearAuthenticationState,
  getAccessTokenAsync,
  retryLastOperation,
  clearMsalStorage,

  KeyForLocalForage,
});

authService.setAuthStoreSetup(() => setup());
